import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Users, Eye, EyeOff, Instagram, Mail, Phone, Plus, X, LayoutGrid, List, Table, ArrowUpDown, ArrowUp, ArrowDown, MapPin, Image, Trash2 } from 'lucide-react';
import { Tooltip } from 'react-tooltip';
import { Person } from '../../types';
import { stalkerAPI, getStaticFileUrl } from '../../services/api';
import { useSearch } from '../../contexts/SearchContext';
import { useDarkMode } from '../../contexts/DarkModeContext';
import loggingService from '../../services/loggingService';

const Persons: React.FC = () => {
	const { globalSearchTerm } = useSearch();
	const { isDark } = useDarkMode();
	const navigate = useNavigate();
	const [persons, setPersons] = useState<Person[]>([]);
	const [loading, setLoading] = useState(true);
	const [showModal, setShowModal] = useState(false);
	const [formData, setFormData] = useState({
		person_pin: '',
		person_name: '',
		person_surname: '',
		person_gender: 'female',
		person_sexualOrientation: 'heterosexual',
		person_avatar: null as File | null,
		person_birthdate: '',
		person_birthCity: '',
		person_birthCountry: '',
		person_hideHome: '0',
		numbers: [] as Array<{ pn_number: string; pn_status: string }>,
		mails: [] as Array<{ pm_mail: string; pm_status: string }>
	});
	const [submitting, setSubmitting] = useState(false);
	const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
	const [showHidden, setShowHidden] = useState(false);
	const [viewType, setViewType] = useState<'horizontal' | 'vertical' | 'table'>('horizontal');
	const [sortField, setSortField] = useState<string | null>(null);
	const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
	const hasLoggedPageView = useRef(false);

	useEffect(() => {
		loadData();
		if (!hasLoggedPageView.current) {
			loggingService.logPageView('/stalker/persons', 'Stalker', 'Persons');
			hasLoggedPageView.current = true;
		}
	}, []);

	const loadData = async () => {
		try {
			const response = await stalkerAPI.persons.getAll();
			setPersons(response.data.data);
		} catch (error) {
			console.error('Error loading persons:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setSubmitting(true);
		try {
			const formDataToSend = new FormData();
			formDataToSend.append('person_pin', formData.person_pin || '');
			formDataToSend.append('person_name', formData.person_name);
			formDataToSend.append('person_surname', formData.person_surname);
			formDataToSend.append('person_gender', formData.person_gender);
			formDataToSend.append('person_sexualOrientation', formData.person_sexualOrientation);
			if (formData.person_avatar) {
				formDataToSend.append('person_avatar', formData.person_avatar);
			}
			formDataToSend.append('person_birthdate', formData.person_birthdate);
			formDataToSend.append('person_birthCity', formData.person_birthCity);
			formDataToSend.append('person_birthCountry', formData.person_birthCountry);
			formDataToSend.append('person_hideHome', formData.person_hideHome);
			
			const response = await stalkerAPI.persons.create(formDataToSend);
			const personId = response.data.data?.person_id;
			if (personId) {
				loggingService.logCreate('person', personId, `${formData.person_name} ${formData.person_surname}`, 'Stalker');
			}
			setShowModal(false);
			setFormData({
				person_pin: '',
				person_name: '',
				person_surname: '',
				person_gender: 'female',
				person_sexualOrientation: 'heterosexual',
				person_avatar: null,
				person_birthdate: '',
				person_birthCity: '',
				person_birthCountry: '',
				person_hideHome: '0',
				numbers: [],
				mails: []
			});
			setAvatarPreview(null);
			loadData();
		} catch (error) {
			console.error('Error creating person:', error);
		} finally {
			setSubmitting(false);
		}
	};


	const calculateAgeAndDaysUntilBirthday = (birthdate: string | undefined) => {
		if (!birthdate) return null;
		const birth = new Date(birthdate);
		const today = new Date();
		let age = today.getFullYear() - birth.getFullYear();
		const monthDiff = today.getMonth() - birth.getMonth();
		if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
			age--;
		}
		
		const nextBirthday = new Date(today.getFullYear(), birth.getMonth(), birth.getDate());
		if (nextBirthday < today) {
			nextBirthday.setFullYear(today.getFullYear() + 1);
		}
		const daysUntil = Math.ceil((nextBirthday.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
		
		// Format date as dd.mm.yyyy
		const day = String(birth.getDate()).padStart(2, '0');
		const month = String(birth.getMonth() + 1).padStart(2, '0');
		const year = birth.getFullYear();
		const formattedDate = `${day}.${month}.${year}`;
		
		return { age, daysUntil, fullDate: formattedDate };
	};

	const filteredPersons = persons.filter(person => {
		const matchesSearch = globalSearchTerm === '' ||
			person.person_name?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			person.person_surname?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			person.person_pin?.toLowerCase().includes(globalSearchTerm.toLowerCase());
		const matchesHideFilter = showHidden || person.person_hideHome !== '1';
		return matchesSearch && matchesHideFilter;
	});

	const sortedPersons = [...filteredPersons].sort((a, b) => {
		if (!sortField) return 0;
		
		let aValue: any = a[sortField as keyof Person];
		let bValue: any = b[sortField as keyof Person];
		
		if (aValue === undefined || aValue === null) aValue = '';
		if (bValue === undefined || bValue === null) bValue = '';
		
		if (typeof aValue === 'string') {
			aValue = aValue.toLowerCase();
			bValue = bValue.toLowerCase();
		}
		
		if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
		if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
		return 0;
	});

	const handleSort = (field: string) => {
		if (sortField === field) {
			if (sortDirection === 'asc') {
				setSortDirection('desc');
			} else if (sortDirection === 'desc') {
				// 3rd click: reset to default
				setSortField(null);
				setSortDirection('asc');
			}
		} else {
			setSortField(field);
			setSortDirection('asc');
		}
	};

	const SortIcon = ({ field }: { field: string }) => {
		if (sortField !== field) {
			return <ArrowUpDown className="w-4 h-4 ml-1" />;
		}
		return sortDirection === 'asc' ? <ArrowUp className="w-4 h-4 ml-1" /> : <ArrowDown className="w-4 h-4 ml-1" />;
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#E50914' }} />
			</div>
		);
	}

	return (
		<div className="space-y-4 px-2 md:px-0">
			<div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
				<div>
					<h1 className={`text-lg font-mono font-bold tracking-widest uppercase ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>TARGET.DATABASE</h1>
					<p className={`text-[10px] font-mono mt-1 ${isDark ? 'text-emerald-500/70' : 'text-emerald-600/70'}`}>
						{globalSearchTerm ? `[ ${sortedPersons.length} / ${filteredPersons.length} ENTITIES MATCHED ]` : `[ ${filteredPersons.length} ENTITIES INDEXED ]`}
					</p>
				</div>
				<div className="flex items-center space-x-2 md:space-x-3 flex-wrap gap-2">
					<div className="flex items-center border rounded-none p-0.5" style={isDark ? { borderColor: '#222', backgroundColor: '#0a0a0a' } : { borderColor: '#e5e5e5', backgroundColor: '#f5f5f5' }}>
						<button
							onClick={() => setViewType('horizontal')}
							className={`p-1.5 rounded-none ${viewType === 'horizontal' ? (isDark ? 'bg-[#111] text-emerald-400 border border-[#333]' : 'bg-neutral-100 text-emerald-600 border border-neutral-300') : (isDark ? 'hover:bg-[#111] text-neutral-500 border border-transparent' : 'hover:bg-neutral-100 text-neutral-500 border border-transparent')}`}
							title="Horizontal Cards"
						>
							<LayoutGrid className="w-3.5 h-3.5" />
						</button>
						<button
							onClick={() => setViewType('vertical')}
							className={`p-1.5 rounded-none ${viewType === 'vertical' ? (isDark ? 'bg-[#111] text-emerald-400 border border-[#333]' : 'bg-neutral-100 text-emerald-600 border border-neutral-300') : (isDark ? 'hover:bg-[#111] text-neutral-500 border border-transparent' : 'hover:bg-neutral-100 text-neutral-500 border border-transparent')}`}
							title="Vertical Cards"
						>
							<List className="w-3.5 h-3.5" />
						</button>
						<button
							onClick={() => setViewType('table')}
							className={`p-1.5 rounded-none ${viewType === 'table' ? (isDark ? 'bg-[#111] text-emerald-400 border border-[#333]' : 'bg-neutral-100 text-emerald-600 border border-neutral-300') : (isDark ? 'hover:bg-[#111] text-neutral-500 border border-transparent' : 'hover:bg-neutral-100 text-neutral-500 border border-transparent')}`}
							title="Table View"
						>
							<Table className="w-3.5 h-3.5" />
						</button>
					</div>
					<button
						onClick={() => setShowModal(true)}
						className={`flex items-center space-x-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider uppercase border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 transition-colors ${isDark ? 'bg-[#111]' : 'bg-white'}`}
					>
						<Plus className="w-3.5 h-3.5" />
						<span>NEW_ENTITY</span>
					</button>
					<button
						onClick={() => setShowHidden(!showHidden)}
						className={`p-1.5 border transition-colors ${isDark ? 'border-[#333] hover:bg-[#111]' : 'border-neutral-300 hover:bg-neutral-100'} ${showHidden ? (isDark ? 'text-red-400' : 'text-red-600') : (isDark ? 'text-neutral-500' : 'text-neutral-500')}`}
						title={showHidden ? 'Hide hidden persons' : 'Show hidden persons'}
					>
						{showHidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
					</button>
				</div>
			</div>

			{viewType === 'horizontal' && (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
					{sortedPersons.map((person) => (
					<div
						key={person.person_id}
						className={`p-4 border ${isDark ? 'bg-[#111] border-[#333] hover:border-emerald-500/50' : 'bg-white border-neutral-300 hover:border-emerald-600/50'} transition-colors cursor-pointer group`}
						onClick={() => navigate(`/stalker/persons/${person.person_id}`)}
					>
						<div className="flex items-center gap-4 mb-4">
							{person.person_avatar ? (
								<img
									src={getStaticFileUrl(person.person_avatar) || ''}
									alt="AVT"
									className={`w-20 h-20 object-cover border ${isDark ? 'border-[#444]' : 'border-neutral-300'} transition-all`}
								/>
							) : (
								<div className={`w-20 h-20 flex items-center justify-center font-mono text-xl border ${isDark ? 'bg-[#1a1a1a] text-neutral-500 border-[#333]' : 'bg-neutral-100 text-neutral-500 border-neutral-300'}`}>
									{(person.person_name || '').charAt(0)}{(person.person_surname || '').charAt(0)}
								</div>
							)}
							<div className="flex-1 min-w-0">
								<h3 className={`text-[14px] font-mono font-bold uppercase truncate ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
									{person.person_name} {person.person_surname}
								</h3>
								{person.person_birthdate && (() => {
									const ageInfo = calculateAgeAndDaysUntilBirthday(person.person_birthdate);
									if (ageInfo) {
										return (
											<p 
												className={`text-[11px] font-mono truncate mt-1 ${isDark ? 'text-emerald-500/60' : 'text-emerald-600/60'}`}
												data-tooltip-id={`birthday-tooltip-${person.person_id}`}
												data-tooltip-content={ageInfo.fullDate}
											>
												AGE:{ageInfo.age} | TTL:{ageInfo.daysUntil}d
											</p>
										);
									}
									return null;
								})()}
							</div>
						</div>

						<div className="grid grid-cols-5 gap-1 border-t pt-3" style={isDark ? { borderColor: '#222' } : { borderColor: '#e5e5e5' }}>
							<div className="flex flex-col items-center">
								<Phone className={`w-4 h-4 mb-1 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`} />
								<span className={`text-[11px] font-mono ${isDark ? 'text-cyan-400/80' : 'text-cyan-600'}`}>{person.numbers?.length || 0}</span>
							</div>
							<div className="flex flex-col items-center">
								<Mail className={`w-4 h-4 mb-1 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`} />
								<span className={`text-[11px] font-mono ${isDark ? 'text-cyan-400/80' : 'text-cyan-600'}`}>{person.mails?.length || 0}</span>
							</div>
							<div className="flex flex-col items-center">
								<MapPin className={`w-4 h-4 mb-1 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`} />
								<span className={`text-[11px] font-mono ${isDark ? 'text-cyan-400/80' : 'text-cyan-600'}`}>{person.locations?.length || 0}</span>
							</div>
							<div className="flex flex-col items-center">
								<Instagram className={`w-4 h-4 mb-1 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`} />
								<span className={`text-[11px] font-mono ${isDark ? 'text-cyan-400/80' : 'text-cyan-600'}`}>
									{(person.instaUsers?.length || 0) + (person.vscoUsers?.length || 0) + (person.tiktokUsers?.length || 0) + (person.xUsers?.length || 0) + (person.snapchatUsers?.length || 0) + (person.spotifyUsers?.length || 0)}
								</span>
							</div>
							<div className="flex flex-col items-center">
								<Image className={`w-4 h-4 mb-1 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`} />
								<span className={`text-[11px] font-mono ${isDark ? 'text-cyan-400/80' : 'text-cyan-600'}`}>{person.mediaCount || 0}</span>
							</div>
						</div>
					</div>
					))}
				</div>
			)}

			{viewType === 'vertical' && (
				<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
					{sortedPersons.map((person) => {
						const ageInfo = person.person_birthdate ? calculateAgeAndDaysUntilBirthday(person.person_birthdate) : null;
						return (
							<div
								key={person.person_id}
								className={`p-4 border ${isDark ? 'bg-[#111] border-[#333] hover:border-emerald-500/50' : 'bg-white border-neutral-300 hover:border-emerald-600/50'} transition-colors cursor-pointer flex flex-col items-center group`}
								onClick={() => navigate(`/stalker/persons/${person.person_id}`)}
							>
								{/* Avatar */}
								<div className="mb-3 relative">
									{person.person_avatar ? (
										<img
											src={getStaticFileUrl(person.person_avatar) || ''}
											alt="AVT"
											className={`w-28 h-28 object-cover border ${isDark ? 'border-[#444]' : 'border-neutral-300'} transition-all`}
										/>
									) : (
										<div className={`w-28 h-28 flex items-center justify-center font-mono text-3xl border ${isDark ? 'bg-[#1a1a1a] text-neutral-500 border-[#333]' : 'bg-neutral-100 text-neutral-500 border-neutral-300'}`}>
											{(person.person_name || '').charAt(0)}{(person.person_surname || '').charAt(0)}
										</div>
									)}
								</div>
								
								{/* Name */}
								<div className="text-center mb-1 w-full px-2">
									<h3 className={`text-[14px] font-mono font-bold uppercase truncate ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
										{person.person_name} {person.person_surname}
									</h3>
								</div>
								
								{/* Age and Birthdate */}
								{ageInfo && (
									<div className="text-center mb-3">
										<p 
											className={`text-[9px] font-mono tracking-wider ${isDark ? 'text-emerald-500/60' : 'text-emerald-600/60'}`}
											data-tooltip-id={`birthday-tooltip-${person.person_id}`}
											data-tooltip-content={`${ageInfo.fullDate} / ${ageInfo.age} yo`}
										>
											TTL: {ageInfo.daysUntil}d
										</p>
									</div>
								)}
								
								{/* Info Icons */}
								<div className="flex items-center justify-center space-x-4 mt-auto pt-3 border-t w-full" style={isDark ? { borderColor: '#222' } : { borderColor: '#e5e5e5' }}>
									<div className="flex flex-col items-center">
										<span className={`text-[11px] font-mono ${isDark ? 'text-cyan-400/80' : 'text-cyan-600'}`}>{person.numbers?.length || 0}</span>
										<Phone className={`w-3.5 h-3.5 mt-1 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`} />
									</div>
									<div className="flex flex-col items-center">
										<span className={`text-[11px] font-mono ${isDark ? 'text-cyan-400/80' : 'text-cyan-600'}`}>{person.mails?.length || 0}</span>
										<Mail className={`w-3.5 h-3.5 mt-1 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`} />
									</div>
									<div className="flex flex-col items-center">
										<span className={`text-[11px] font-mono ${isDark ? 'text-cyan-400/80' : 'text-cyan-600'}`}>{person.locations?.length || 0}</span>
										<MapPin className={`w-3.5 h-3.5 mt-1 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`} />
									</div>
									<div className="flex flex-col items-center">
										<span className={`text-[11px] font-mono ${isDark ? 'text-cyan-400/80' : 'text-cyan-600'}`}>
											{(person.instaUsers?.length || 0) + (person.vscoUsers?.length || 0) + (person.tiktokUsers?.length || 0) + (person.xUsers?.length || 0) + (person.snapchatUsers?.length || 0) + (person.spotifyUsers?.length || 0)}
										</span>
										<Instagram className={`w-3.5 h-3.5 mt-1 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`} />
									</div>
								</div>
							</div>
						);
					})}
				</div>
			)}

			{viewType === 'table' && (
				<div className={`border rounded-none overflow-hidden ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-white border-neutral-200'}`}>
					<div className="overflow-x-auto">
						<table className="w-full">
							<thead className={isDark ? 'bg-[#111] border-b border-[#222]' : 'bg-neutral-50 border-b border-neutral-200'}>
								<tr>
									<th className={`px-4 py-3 text-left text-[10px] font-mono tracking-wider uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-500'}`}>
										AVT
									</th>
									<th className={`px-4 py-3 text-left text-[10px] font-mono tracking-wider uppercase ${isDark ? 'text-neutral-500 hover:text-neutral-300' : 'text-neutral-500 hover:text-neutral-700'} cursor-pointer transition-colors`} onClick={() => handleSort('person_name')}>
										<div className="flex items-center">
											NAME
											<SortIcon field="person_name" />
										</div>
									</th>
									<th className={`px-4 py-3 text-left text-[10px] font-mono tracking-wider uppercase ${isDark ? 'text-neutral-500 hover:text-neutral-300' : 'text-neutral-500 hover:text-neutral-700'} cursor-pointer transition-colors`} onClick={() => handleSort('person_surname')}>
										<div className="flex items-center">
											SURNAME
											<SortIcon field="person_surname" />
										</div>
									</th>
									<th className={`px-4 py-3 text-left text-[10px] font-mono tracking-wider uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-500'}`}>
										STATS
									</th>
									<th className={`px-4 py-3 text-left text-[10px] font-mono tracking-wider uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-500'}`}>
										ACTION
									</th>
								</tr>
							</thead>
							<tbody className={`divide-y ${isDark ? 'divide-[#222]' : 'divide-neutral-200'}`}>
								{sortedPersons.map((person) => (
									<tr 
										key={person.person_id} 
										className={`group ${isDark ? 'hover:bg-[#111]' : 'hover:bg-neutral-50'} transition-colors cursor-pointer`}
										onClick={() => navigate(`/stalker/persons/${person.person_id}`)}
									>
										<td className={`px-4 py-2 whitespace-nowrap`}>
											{person.person_avatar ? (
												<img
													src={getStaticFileUrl(person.person_avatar) || ''}
													alt="A"
													className={`w-8 h-8 object-cover border ${isDark ? 'border-[#333]' : 'border-neutral-300'} transition-all`}
												/>
											) : (
												<div className={`w-8 h-8 flex items-center justify-center font-mono text-[10px] border ${isDark ? 'bg-[#1a1a1a] text-neutral-500 border-[#333]' : 'bg-neutral-100 text-neutral-500 border-neutral-300'}`}>
													{(person.person_name || '').charAt(0)}{(person.person_surname || '').charAt(0)}
												</div>
											)}
										</td>
										<td className={`px-4 py-3 whitespace-nowrap text-sm font-mono font-bold uppercase ${isDark ? 'text-neutral-300 group-hover:text-emerald-400' : 'text-neutral-700 group-hover:text-emerald-700'} transition-colors`}>
											{person.person_name || '-'}
										</td>
										<td className={`px-4 py-3 whitespace-nowrap text-sm font-mono font-bold uppercase ${isDark ? 'text-neutral-300 group-hover:text-emerald-400' : 'text-neutral-700 group-hover:text-emerald-700'} transition-colors`}>
											{person.person_surname || '-'}
										</td>
										<td className={`px-4 py-2 whitespace-nowrap text-[10px] font-mono ${isDark ? 'text-neutral-500' : 'text-neutral-500'}`}>
											<div className="flex items-center space-x-3">
												<div className="flex items-center space-x-1" title="Numbers">
													<Phone className={`w-3 h-3 ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`} />
													<span className={isDark ? 'text-cyan-500' : 'text-cyan-700'}>{person.numbers?.length || 0}</span>
												</div>
												<div className="flex items-center space-x-1" title="Emails">
													<Mail className={`w-3 h-3 ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`} />
													<span className={isDark ? 'text-cyan-500' : 'text-cyan-700'}>{person.mails?.length || 0}</span>
												</div>
												<div className="flex items-center space-x-1" title="Locations">
													<MapPin className={`w-3 h-3 ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`} />
													<span className={isDark ? 'text-cyan-500' : 'text-cyan-700'}>{person.locations?.length || 0}</span>
												</div>
												<div className="flex items-center space-x-1" title="Socials">
													<Instagram className={`w-3 h-3 ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`} />
													<span className={isDark ? 'text-cyan-500' : 'text-cyan-700'}>
														{(person.instaUsers?.length || 0) + (person.vscoUsers?.length || 0) + (person.tiktokUsers?.length || 0) + (person.xUsers?.length || 0) + (person.snapchatUsers?.length || 0) + (person.spotifyUsers?.length || 0)}
													</span>
												</div>
											</div>
										</td>
										<td className={`px-4 py-2 whitespace-nowrap text-[10px] font-mono`}>
											<button
												onClick={async (e) => {
													e.stopPropagation();
													if (!window.confirm(`Are you sure you want to delete ${person.person_name} ${person.person_surname}? This action cannot be undone.`)) return;
													try {
														await stalkerAPI.persons.delete(person.person_id);
														loggingService.logDelete('person', person.person_id, `${person.person_name} ${person.person_surname}`, 'Stalker');
														loadData();
													} catch (error) {
														console.error('Error deleting person:', error);
													}
												}}
												className={`flex items-center justify-center p-1.5 border transition-colors ${isDark ? 'border-red-900/30 text-red-500 hover:bg-red-500/10' : 'border-red-200 text-red-600 hover:bg-red-50'} opacity-0 group-hover:opacity-100`}
												title="Delete Entity"
											>
												<Trash2 className="w-3.5 h-3.5" />
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}
			{persons.map(person => person.person_birthdate && (
				<Tooltip key={`birthday-tooltip-${person.person_id}`} id={`birthday-tooltip-${person.person_id}`} />
			))}

			{sortedPersons.length === 0 && (
				<div className={`flex flex-col items-center justify-center py-20 border border-dashed ${isDark ? 'border-[#333] bg-[#0a0a0a]' : 'border-neutral-300 bg-neutral-50'}`}>
					<div className={`p-4 rounded-full mb-4 ${isDark ? 'bg-[#111]' : 'bg-neutral-200'}`}>
						<Users className={`w-8 h-8 ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`} />
					</div>
					<h3 className={`text-sm font-mono tracking-widest ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>NO_ENTITIES_FOUND</h3>
					<p className={`text-[10px] font-mono mt-2 mb-6 ${isDark ? 'text-neutral-600' : 'text-neutral-500'}`}>The database query returned 0 results.</p>
					<button
						onClick={() => setShowModal(true)}
						className={`flex items-center space-x-1.5 px-4 py-2 text-[10px] font-mono tracking-wider uppercase border text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10 transition-colors ${isDark ? 'bg-[#111]' : 'bg-white'}`}
					>
						<Plus className="w-3.5 h-3.5" />
						<span>INITIALIZE NEW_ENTITY</span>
					</button>
				</div>
			)}

			{/* Create Person Modal */}
			{showModal && createPortal(
				<div
					className="fixed inset-0 bg-black/80 flex items-center justify-center z-[1005] p-2 overflow-y-auto"
					onClick={() => !submitting && setShowModal(false)}
				>
					<div
						className={`rounded-none border p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-white border-neutral-300'}`}
						onClick={(e) => e.stopPropagation()}
					>
						<div className="flex justify-between items-center mb-6 border-b pb-4 border-emerald-500/20">
							<h2 className={`text-xs font-mono tracking-[0.2em] font-bold uppercase ${isDark ? 'text-emerald-500' : 'text-emerald-700'}`}>
								// INITIALIZE: NEW_TARGET_ENTITY
							</h2>
							<button
								onClick={() => setShowModal(false)}
								className={`p-1 transition-colors ${isDark ? 'hover:bg-emerald-500/10 text-neutral-500' : 'hover:bg-neutral-100 text-neutral-400'}`}
							>
								<X className="w-4 h-4" />
							</button>
						</div>
						<form onSubmit={handleSubmit} className="space-y-6" encType="multipart/form-data">
							<div>
								<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
									PIN_IDENTIFIER
								</label>
								<input
									type="text"
									name="person_pin"
									value={formData.person_pin}
									onChange={(e) => setFormData({ ...formData, person_pin: e.target.value })}
									className={`w-full px-3 py-2 rounded-none border font-mono text-xs transition-colors ${isDark ? 'bg-[#111] border-[#333] text-white focus:border-emerald-500' : 'bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-emerald-500'}`}
									placeholder="####"
								/>
							</div>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div className="space-y-2">
									<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
										FIRST_NAME <span className="text-red-500">*</span>
									</label>
									<input
										type="text"
										name="person_name"
										value={formData.person_name}
										onChange={(e) => setFormData({ ...formData, person_name: e.target.value })}
										className={`w-full px-3 py-2 rounded-none border font-mono text-xs transition-colors ${isDark ? 'bg-[#111] border-[#333] text-white focus:border-emerald-500' : 'bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-emerald-500'}`}
										required
									/>
								</div>
								<div className="space-y-2">
									<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
										LAST_NAME <span className="text-red-500">*</span>
									</label>
									<input
										type="text"
										name="person_surname"
										value={formData.person_surname}
										onChange={(e) => setFormData({ ...formData, person_surname: e.target.value })}
										className={`w-full px-3 py-2 rounded-none border font-mono text-xs transition-colors ${isDark ? 'bg-[#111] border-[#333] text-white focus:border-emerald-500' : 'bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-emerald-500'}`}
										required
									/>
								</div>
							</div>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div className="space-y-2">
									<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
										GENDER_CLASS
									</label>
									<select
										name="person_gender"
										value={formData.person_gender}
										onChange={(e) => setFormData({ ...formData, person_gender: e.target.value })}
										className={`w-full px-3 py-2 rounded-none border font-mono text-xs transition-colors ${isDark ? 'bg-[#111] border-[#333] text-white focus:border-emerald-500' : 'bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-emerald-500'}`}
									>
										<option value="female">FEMALE</option>
										<option value="male">MALE</option>
										<option value="other">OTHER</option>
									</select>
								</div>
								<div className="space-y-2">
									<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>ORIENTATION</label>
									<select
										name="person_sexualOrientation"
										value={formData.person_sexualOrientation}
										onChange={(e) => setFormData({ ...formData, person_sexualOrientation: e.target.value })}
										className={`w-full px-3 py-2 rounded-none border font-mono text-xs transition-colors ${isDark ? 'bg-[#111] border-[#333] text-white focus:border-emerald-500' : 'bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-emerald-500'}`}
									>
										<option value="heterosexual">HETEROSEXUAL</option>
										<option value="homosexual">HOMOSEXUAL</option>
										<option value="bisexual">BISEXUAL</option>
										<option value="other">OTHER</option>
									</select>
								</div>
							</div>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div className="space-y-2">
									<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>BIRTH_DATE</label>
									<input
										type="date"
										name="person_birthdate"
										value={formData.person_birthdate}
										onChange={(e) => setFormData({ ...formData, person_birthdate: e.target.value })}
										className={`w-full px-3 py-2 rounded-none border font-mono text-xs transition-colors ${isDark ? 'bg-[#111] border-[#333] text-white focus:border-emerald-500' : 'bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-emerald-500'}`}
									/>
								</div>
								<div className="space-y-2 text-right">
									<label className={`block text-[10px] font-mono tracking-widest uppercase mb-4 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>STEALTH_FLAG</label>
									<button
										type="button"
										onClick={() => setFormData({ ...formData, person_hideHome: formData.person_hideHome === '1' ? '0' : '1' })}
										className={`relative inline-flex h-5 w-10 items-center rounded-none transition-colors border ml-auto ${
											formData.person_hideHome === '1' ? 'bg-red-500/20 border-red-500' : 'bg-neutral-800 border-neutral-700'
										}`}
									>
										<span
											className={`inline-block h-3 w-3 transform rounded-none transition-transform ${
												formData.person_hideHome === '1' ? 'translate-x-6 bg-red-500' : 'translate-x-1 bg-neutral-500'
											}`}
										/>
									</button>
								</div>
							</div>
							<div className="space-y-2">
								<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>AVATAR_INIT</label>
								<div className="flex items-start space-x-6">
									{avatarPreview && (
										<div className="relative border border-emerald-500/30 p-1 bg-black">
											<img src={avatarPreview} alt="Preview" className="w-20 h-20 object-cover" />
											<button
												type="button"
												onClick={() => { setFormData({ ...formData, person_avatar: null }); setAvatarPreview(null); }}
												className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
											>
												<X className="w-3 h-3" />
											</button>
										</div>
									)}
									<div className="flex-1">
										<label
											className={`flex flex-col items-center justify-center w-full h-20 border border-dashed transition-colors cursor-pointer ${
												isDark ? 'border-[#333] hover:border-emerald-500/50 bg-[#111]' : 'border-neutral-300 hover:border-emerald-500/50 bg-neutral-50'
											}`}
										>
											<p className="text-[10px] font-mono tracking-wide text-neutral-500 uppercase">
												{formData.person_avatar ? formData.person_avatar.name : 'CLICK_OR_DROP_IMG'}
											</p>
											<input
												type="file"
												name="person_avatar"
												accept="image/*"
												className="hidden"
												onChange={(e) => {
													const file = e.target.files?.[0] || null;
													if (file) {
														setFormData({ ...formData, person_avatar: file });
														const reader = new FileReader();
														reader.onloadend = () => setAvatarPreview(reader.result as string);
														reader.readAsDataURL(file);
													}
												}}
											/>
										</label>
									</div>
								</div>
							</div>
							<div className="flex justify-end space-x-3 pt-4">
								<button
									type="button"
									onClick={() => setShowModal(false)}
									className={`px-4 py-2 border font-mono text-[10px] tracking-widest transition-colors ${isDark ? 'border-[#333] text-neutral-500 hover:bg-[#222]' : 'bg-neutral-100 text-neutral-600 border-neutral-300 hover:bg-neutral-200'}`}
								>
									ABORT
								</button>
								<button
									type="submit"
									disabled={submitting}
									className="px-6 py-2 border border-emerald-500/50 bg-emerald-500/10 text-emerald-500 font-mono text-[10px] tracking-[0.2em] font-bold hover:bg-emerald-500 hover:text-black transition-all disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{submitting ? 'PROCESSING...' : 'EXEC: COMMIT'}
								</button>
							</div>
						</form>
					</div>
				</div>,
				document.body
			)}
		</div>
	);
};

export default Persons;

